import { GoodsCard2Column } from '@banhala/ui-kit';
import type { TwoColBrandGoodsListProps } from './types';
import { bridge, isNotNil, useNavigator } from '../../../../utils';
import { useCallback, useEffect } from 'react';
import { useRemoveLikeGoods } from '../../../../queries';
import { useAddLikeGoods } from '../../../../hooks';
import type { GoodsCoreModel, IScreensLogging } from '../../../../api/types';
import { getTwoColGoodsListData } from '../TwoColGoodsList/getTwoColGoodsListData';
import { useLogOnGoodsPressItem } from '../TwoColGoodsList/log/logOnGoodsPressItem';
import { useLogOnPressLike } from '../TwoColGoodsList/log/logOnPressLike';

export const TwoColBrandGoodsList = ({
  entity,
  likeMap,
  setLikeMap,
}: TwoColBrandGoodsListProps) => {
  const { navigator } = useNavigator();
  const logOnGoodsPressItem = useLogOnGoodsPressItem();
  const logOnPressLike = useLogOnPressLike();
  const { mutateAsync: mutateRemoveLikeGoods } = useRemoveLikeGoods();
  const { addLikeGoods } = useAddLikeGoods({
    listId: entity?.[0]?.logging?.analytics?.LIST_ID as string,
    listParams: entity?.[0]?.logging?.analytics?.LIST_ID as string | undefined,
    onChangeIsLiked: (sno, nextValue) => {
      setLikeMap(prevState => ({ ...prevState, [sno]: nextValue }));
    },
    screen:
      (entity?.[0]?.logging?.analytics?.SCREEN as string | undefined) ?? '',
  });

  useEffect(() => {
    setLikeMap(prevState => {
      return {
        ...prevState,
        ...entity?.reduce<Record<string, boolean>>((acc, cur) => {
          acc[cur.item.sno] = cur.item.like?.is_liked ?? false;
          return acc;
        }, {}),
      };
    });
  }, [entity, setLikeMap]);

  const handlePressItem = useCallback(
    ({
      item,
      logging: itemLogging,
    }: {
      item: GoodsCoreModel;
      logging: IScreensLogging;
    }) => {
      logOnGoodsPressItem({ item, logging: itemLogging });
      navigator.moveToGoods({
        sno: item.sno,
        firstPageRendering: item.first_page_rendering ?? null,
        loggingAnalytics: itemLogging?.analytics,
        ad: item.ad,
      });
    },
    [logOnGoodsPressItem, navigator],
  );

  const handlePressLike = useCallback(
    async ({
      item,
      logging: itemLogging,
    }: {
      item: GoodsCoreModel;
      logging: IScreensLogging;
    }) => {
      const currentLike = likeMap[item.sno] ?? false;
      logOnPressLike({ item, logging: itemLogging, currentLike });
      if (currentLike) {
        await mutateRemoveLikeGoods({
          sno: item.sno,
          list_params_referrer: itemLogging?.analytics?.LIST_ID as
            | string
            | undefined,
        });
        bridge.likeGoods({ goods_sno: item.sno, is_liked: false });
        setLikeMap(prevState => ({ ...prevState, [item.sno]: false }));
        return;
      }
      addLikeGoods(
        item.sno,
        itemLogging?.analytics,
        itemLogging?.ably_only_log,
      );
    },
    [likeMap, logOnPressLike, mutateRemoveLikeGoods, setLikeMap, addLikeGoods],
  );

  if (!entity || entity.length === 0) return null;

  const goodsData = getTwoColGoodsListData(entity as never);

  return (
    <GoodsCard2Column
      likeMap={likeMap}
      showRanking={
        entity?.some(item => isNotNil(item.render?.data.ranking)) ?? false
      }
      list={goodsData}
      onPressItem={handlePressItem}
      onPressLike={handlePressLike}
      keyExtractor={(item, index) => (item.sno ?? index).toString()}
    />
  );
};
